/**
 * Native API helper for Capacitor
 * Uses CapacitorHttp (NSURLSession) on native, fetch() on web
 * With CapacitorHttp.enabled=true in capacitor.config.ts,
 * ALL fetch() calls on iOS are automatically routed through native networking.
 */

import { isNativePlatform, getBackendUrl } from './platform';

let CapacitorHttp = null;

// Try to import CapacitorHttp at module load time
try {
  const cap = require('@capacitor/core');
  CapacitorHttp = cap.CapacitorHttp;
} catch (e) {
  // @capacitor/core not available (SSR or non-Capacitor build)
}

const getApiBase = () => {
  if (isNativePlatform()) return 'https://www.10minvestor.com/api';
  const backend = getBackendUrl();
  return backend ? `${backend}/api` : '/api';
};

/**
 * Make a POST request using native HTTP on mobile, fetch on web
 */
export const nativePost = async (path, body, token) => {
  const url = `${getApiBase()}${path}`;

  // Try native CapacitorHttp first on mobile
  if (isNativePlatform() && CapacitorHttp) {
    try {
      const headers = { 'Content-Type': 'application/json' };
      if (token) headers['Authorization'] = `Bearer ${token}`;

      const response = await CapacitorHttp.post({
        url,
        headers,
        data: body,
      });

      if (response.status >= 200 && response.status < 300) {
        return typeof response.data === 'string' ? JSON.parse(response.data) : response.data;
      }

      const errData = typeof response.data === 'string' ? JSON.parse(response.data) : response.data;
      const err = new Error(errData?.detail || `Erreur ${response.status}`);
      err.response = { status: response.status, data: errData };
      throw err;
    } catch (e) {
      if (e.response) throw e;
      // CapacitorHttp failed unexpectedly, fall through to fetch
      console.warn('[nativePost] CapacitorHttp failed, falling back to fetch:', e.message);
    }
  }

  // Fallback: use fetch (on iOS with CapacitorHttp.enabled=true, this also uses native networking)
  const headers = { 'Content-Type': 'application/json' };
  if (token) headers['Authorization'] = `Bearer ${token}`;

  const resp = await fetch(url, {
    method: 'POST',
    headers,
    body: JSON.stringify(body),
  });

  if (!resp.ok) {
    const errBody = await resp.json().catch(() => ({ detail: `Erreur ${resp.status}` }));
    const err = new Error(errBody?.detail || `Erreur ${resp.status}`);
    err.response = { status: resp.status, data: errBody };
    throw err;
  }

  return await resp.json();
};

/**
 * Make a GET request using native HTTP on mobile, fetch on web
 */
export const nativeGet = async (path, token) => {
  const url = `${getApiBase()}${path}`;

  // Try native CapacitorHttp first on mobile
  if (isNativePlatform() && CapacitorHttp) {
    try {
      const headers = {};
      if (token) headers['Authorization'] = `Bearer ${token}`;

      const response = await CapacitorHttp.get({ url, headers });

      if (response.status >= 200 && response.status < 300) {
        return typeof response.data === 'string' ? JSON.parse(response.data) : response.data;
      }

      return null; // Non-200 = not authenticated
    } catch (e) {
      console.warn('[nativeGet] CapacitorHttp failed, falling back to fetch:', e.message);
    }
  }

  // Fallback: use fetch
  const headers = {};
  if (token) headers['Authorization'] = `Bearer ${token}`;

  const resp = await fetch(url, { headers });

  if (!resp.ok) return null;
  return await resp.json();
};
